import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormProvider as ReactHookFormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ApolloErrorLike, getGraphQLFieldErrors } from 'src/helpers/errorHandling';
import { useTrack } from 'src/lib/analytics';
import {
    AnalyticsElementType,
    AnalyticsEvent,
    AnalyticsEventType,
    ClickElementType,
    DisplayElementType,
} from 'src/lib/interactionTypes';
import type { FormValues } from 'src/lib/components/form/formTypes';
import { FormContext } from './useForm';
import { getElementUnfocusedValueMetadata, shouldTrackElementUnfocusedValue } from './analytics';
import type { LastTrackedElementUnfocusedValue } from './analytics';

import { useScrollToError } from './useForm';
interface Props extends React.DetailedHTMLProps<
    React.FormHTMLAttributes<HTMLFormElement>,
    HTMLFormElement
> {
    apolloError?: ApolloErrorLike;
    value: FormContext<any>;
    children: React.ReactNode;
    validationErrorMessage?: string;
}

interface FieldData {
    required: boolean;
    hasDeps: boolean;
}

const FormMetadataContext = React.createContext<any>({});
const ResqFormContext = React.createContext<FormContext<any> | null>(null);

export const useFormMetaData = () => {
    const context = React.useContext(FormMetadataContext);
    return context;
};

export const useResqFormContext = <T extends FormValues>() => {
    const context = React.useContext(ResqFormContext);
    if (!context) {
        throw new Error('useResqFormContext must be used within <FormProvider />');
    }
    return context as FormContext<T>;
};

export default function FormProvider({
    value,
    apolloError,
    children,
    validationErrorMessage,
}: Props) {
    const { values, validationSchemaZod, submitCount, isValid, errors, isSubmitting } = value;
    const lastTrackedValueRef = useRef<LastTrackedElementUnfocusedValue>(null);

    const { t } = useTranslation('validation');
    const { enqueueSnackbar } = useSnackbar();
    const previousSubmitCountRef = useRef(0);
    const { containerRef } = useScrollToError(isSubmitting);

    const numberOfErrors = Object.keys(errors).length;
    const baseSnackbarErrorMessage = numberOfErrors
        ? t('formErrorCountMessage', { count: numberOfErrors })
        : t('formValidationErrorMessage');
    const debugErrorSuffix =
        import.meta.env.DEV && numberOfErrors
            ? (() => {
                  const errorKeys = Object.keys(errors).join(', ');
                  const expectedServiceTimelineKey = (values as any)?.expectedServiceTimeline?.key;
                  const expectedServiceTimelineSuffix = expectedServiceTimelineKey
                      ? ` timeline=${expectedServiceTimelineKey}`
                      : '';
                  return ` (${errorKeys}${expectedServiceTimelineSuffix})`;
              })()
            : '';
    const snackbarErrorMessage = validationErrorMessage
        ? validationErrorMessage
        : `${baseSnackbarErrorMessage}${debugErrorSuffix}`;

    const track = useTrack();

    // useCallback required - used in useEffect dependency arrays.
    // Without stable reference, effects re-run on every render.
    const trackFieldEvent = useCallback(
        (
            field: string,
            label: string | null,
            role: string | null,
            value: string,
            additionalData?: Record<string, any>,
        ) => {
            const valueMetadata = getElementUnfocusedValueMetadata(value);

            return track(AnalyticsEvent.ElementUnfocused, {
                field,
                ...valueMetadata,
                eventType: AnalyticsEventType.ANALYTICS,
                elementType: role as AnalyticsElementType,
                label: label,
                option: null,
                link: null,
                ...additionalData,
            });
        },
        [track],
    );

    // useCallback required - used in useEffect dependency arrays.
    // Without stable reference, effects re-run on every render.
    const trackErrorEvent = useCallback(
        (errorMessage: string, additionalData?: Record<string, any>) => {
            return track(AnalyticsEvent.ElementDisplayed, {
                errorMessage,
                eventType: AnalyticsEventType.ANALYTICS,
                elementType: DisplayElementType.FORM_ERROR,
                label: null,
                option: null,
                link: null,
                ...additionalData,
            });
        },
        [track],
    );

    const handleBlur = (
        fieldName: string | null | undefined,
        label: string | null | undefined,
        role: string | null | undefined,
        value: string,
    ) => {
        if (!fieldName || value == null) {
            return;
        }
        if (
            !shouldTrackElementUnfocusedValue({
                field: fieldName,
                value,
                lastTracked: lastTrackedValueRef.current,
            })
        ) {
            return;
        }
        lastTrackedValueRef.current = { field: fieldName, value };
        trackFieldEvent(fieldName, label ?? null, role ?? null, value);
    };

    useEffect(() => {
        if (!isValid && submitCount > previousSubmitCountRef.current) {
            enqueueSnackbar(snackbarErrorMessage, { variant: 'error' });
            // This has to be inside the conditional because isValid
            // updates after submitCount
            trackErrorEvent(JSON.stringify(errors));
            previousSubmitCountRef.current = submitCount;
        }
    }, [submitCount, isValid, errors, enqueueSnackbar, snackbarErrorMessage, trackErrorEvent]);

    const [lastApolloError, setLastApolloError] = useState('');
    const [metaData, setMetaData] = useState<any>({});
    const apolloErrorStr = JSON.stringify(apolloError);

    useEffect(() => {
        //when loading flips true to false set the apollo error, if there is one
        if (lastApolloError === apolloErrorStr) {
            return;
        }
        const formattedErrors = {} as any;
        if (apolloError?.graphQLErrors) {
            const fieldErrors = getGraphQLFieldErrors(apolloError);
            Object.entries(fieldErrors).forEach(([key, error]) => {
                formattedErrors[key] = {
                    value: values[key],
                    error,
                };
                trackErrorEvent(apolloErrorStr);
            });
            setMetaData({ ...metaData, apolloErrors: formattedErrors });
        }
        setLastApolloError(apolloErrorStr);
    }, [
        metaData,
        setMetaData,
        lastApolloError,
        values,
        apolloErrorStr,
        apolloError,
        trackFieldEvent,
        trackErrorEvent,
    ]);

    useEffect(() => {
        // Determine which fields are required according to the schema, which may include superRefine() clauses
        // that require the forms current state to be evaluated.
        const schema =
            typeof validationSchemaZod === 'function' ? validationSchemaZod() : validationSchemaZod;
        const objSchema = schema as { shape?: Record<string, any> } | undefined;
        if (!objSchema?.shape) {
            return;
        }
        const lastSchemaCheck = metaData?.lastSchemaCheck;
        if (lastSchemaCheck !== undefined && !lastSchemaCheck.hasDeps) {
            return;
        }
        const valuesJson = JSON.stringify(values);
        if (lastSchemaCheck?.valuesJson === valuesJson) {
            return;
        }
        const requiredFields: { [key: string]: FieldData } = metaData?.requiredFields || {};
        let anyDeps = false;

        // For Zod, we need to check the schema shape
        const shape = objSchema.shape || {};
        for (const fieldName in shape) {
            if (
                !(fieldName in requiredFields) ||
                (fieldName in requiredFields && requiredFields[fieldName].hasDeps)
            ) {
                const fieldSchema = shape[fieldName];
                const def = (fieldSchema as any)?._zod?.def;
                const checks = (def?.checks ?? []) as any[];
                const hasDeps =
                    def?.check === 'custom' ||
                    checks.some((check) => (check as any)?._zod?.def?.check === 'custom');
                anyDeps = anyDeps || hasDeps;

                // Check if field is required (not optional and not nullable)
                const isOptional = fieldSchema?.safeParse?.(undefined)?.success ?? false;
                const isNullable = fieldSchema?.safeParse?.(null)?.success ?? false;
                requiredFields[fieldName] = {
                    required: !isOptional && !isNullable,
                    hasDeps,
                };
            }
        }
        setMetaData({
            ...metaData,
            requiredFields,
            lastSchemaCheck: { valuesJson, hasDeps: anyDeps },
        });
    }, [metaData, setMetaData, values, validationSchemaZod]);

    return (
        <ReactHookFormProvider {...value.rhf}>
            <ResqFormContext.Provider value={value}>
                <FormMetadataContext.Provider value={metaData}>
                    <div ref={containerRef}>
                        {React.Children.map(children, (child) =>
                            React.isValidElement<React.HTMLProps<HTMLInputElement>>(child)
                                ? React.cloneElement(child, {
                                      onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                                          // finds the nearest ancestor with the attributes
                                          const fieldElement =
                                              e.target && typeof e.target.closest === 'function'
                                                  ? (e.target.closest(
                                                        '[data-field-name], [data-field-label], [data-field-role]',
                                                    ) as HTMLElement)
                                                  : null;

                                          const dataFieldLabel = fieldElement
                                              ? fieldElement.dataset['fieldLabel']
                                              : null;
                                          const dataFieldRole = fieldElement
                                              ? fieldElement.dataset['fieldRole']
                                              : null;
                                          const dataFieldName = fieldElement
                                              ? fieldElement.dataset['fieldName']
                                              : null;

                                          if (dataFieldRole == ClickElementType.DROPDOWN_OPTION) {
                                              // Our select dropdown is coded as a button and isn't a real input field,
                                              // so we're not able to access its value properly
                                              // this needs to wait for a rerender
                                              setTimeout(() => {
                                                  return handleBlur(
                                                      dataFieldName,
                                                      dataFieldLabel,
                                                      dataFieldRole,
                                                      e.target.innerText,
                                                  );
                                              }, 1000);
                                          } else {
                                              handleBlur(
                                                  dataFieldName,
                                                  dataFieldLabel,
                                                  dataFieldRole,
                                                  e.target.value,
                                              );
                                          }
                                      },
                                  })
                                : child,
                        )}
                    </div>
                </FormMetadataContext.Provider>
            </ResqFormContext.Provider>
        </ReactHookFormProvider>
    );
}
